import React from "react";
import {
  CssBaseline,
  Container,
  Typography,
  Box,
  Divider,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import Seo from "../components/Seo";
import AboutCTA from "./AboutUs/AboutCTA";

// Premium PDF-like theme
const theme = createTheme({
  palette: {
    primary: { main: "#1a237e" },
    secondary: { main: "#d50000" },
    background: { default: "#ffffff" },
    text: { primary: "#1e1e1e", secondary: "#555" },
  },
  typography: {
    h4: { fontWeight: 700, fontSize: "2rem", marginBottom: "0.5em" },
    h5: {
      fontWeight: 600,
      fontSize: "1.25rem",
      marginTop: "1.5em",
      marginBottom: "0.5em",
    },
    body1: { lineHeight: 1.8, fontSize: "1rem", color: "#1e1e1e" },
    body2: { lineHeight: 1.6, fontSize: "0.95rem", color: "#555" },
  },
});

const Section = ({ title, content }) => (
  <Box component="section" sx={{ mb: 3 }}>
    <Typography variant="h5" component="h2">
      {title}
    </Typography>
    {Array.isArray(content) ? (
      content.map((paragraph, idx) => (
        <Typography variant="body1" paragraph key={idx}>
          {paragraph}
        </Typography>
      ))
    ) : (
      <Typography variant="body1" paragraph sx={{ whiteSpace: "pre-line" }}>
        {content}
      </Typography>
    )}
  </Box>
);

const TermsAndConditionsPage = () => {
  return (
    <Box>
      <Seo
        title="Terms & Conditions | Burj Al Yarmouk Industries LLC"
        description="Read the terms and conditions for using Burj Al Yarmouk Industries LLC’s website, products, fabrication services, and customer guidelines. View legal obligations and user responsibilities."
        url="https://burjalyarmouk.com/terms-and-conditions"
        image="https://burjalyarmouk.com/og-banner.jpg"
        keywords="terms and conditions, fabrication terms, service policies UAE, Burj Al Yarmouk Industries LLC terms, industrial service terms, legal conditions UAE"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Terms & Conditions",
          url: "https://burjalyarmouk.com/terms-and-conditions",
          publisher: {
            "@type": "Organization",
            name: "Burj Al Yarmouk Industries LLC",
            url: "https://burjalyarmouk.com/",
            logo: "https://burjalyarmouk.com/favicon.ico",
            sameAs: [
              "https://www.instagram.com/burjalyarmouk/",
              "https://www.linkedin.com/in/burj-al-yarmouk-industries-llc-417570347/",
            ],
          },
          description:
            "Terms & Conditions for Burj Al Yarmouk Industries LLC. This page outlines the rules, responsibilities, and legal guidelines for using our website and services.",
        }}
      />

      <CssBaseline />
      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Typography variant="h4" component="h1" color="primary">
            Burj AI — Terms & Conditions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: December 2, 2025
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            color="secondary"
            sx={{ fontStyle: "italic" }}
          >
            Important: This Terms & Conditions is for Burj AI’s platform and
            services. It is not legal advice. Consult a lawyer for your
            jurisdiction.
          </Typography>
        </Box>

        {/* Sections */}
        <Box component="main">
          {termsData.sections.map((section, idx) => (
            <Section key={idx} {...section} />
          ))}
        </Box>

        {/* Footer */}
        <Divider sx={{ mt: 6, mb: 2 }} />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          © 2025 Burj AI. All rights reserved.
        </Typography>
      </Container>
      <AboutCTA />
    </Box>
  );
};

// Burj AI Terms Data
const termsData = {
  sections: [
    {
      title: "1. Introduction",
      content:
        "Welcome to Burj AI. By accessing or using our platform and services, you agree to be bound by these Terms & Conditions. Please do not use our services if you do not accept these Terms.",
    },
    {
      title: "2. Platform Access & Accounts",
      content: [
        "2.1 You must provide accurate and current information when creating an account.",
        "2.2 Maintain the confidentiality of your login credentials. Notify us immediately of any unauthorized use.",
      ],
    },
    {
      title: "3. AI Services & Usage",
      content: [
        "3.1 Burj AI provides AI-driven tools and solutions. You agree to use them responsibly and in compliance with all applicable laws.",
        "3.2 You must not attempt to reverse-engineer, exploit, or misuse our AI models or platform features.",
      ],
    },
    {
      title: "4. Intellectual Property",
      content: [
        "All content, AI models, software, logos, and designs provided by Burj AI are owned by Burj AI and protected by law. Unauthorized use is prohibited.",
      ],
    },
    {
      title: "5. Payment & Subscription",
      content: [
        "5.1 Paid services or subscriptions are billed according to the selected plan.",
        "5.2 Cancellation policies vary based on subscription terms. Refunds, if any, are subject to our discretion.",
      ],
    },
    {
      title: "6. Privacy & Data Protection",
      content: [
        "Your data is collected and processed according to our Privacy Policy. By using Burj AI, you consent to this collection and usage.",
      ],
    },
    {
      title: "7. Disclaimers & Limitation of Liability",
      content: [
        "The platform and services are provided 'as is'. Burj AI is not liable for indirect, incidental, or consequential damages arising from platform usage.",
      ],
    },
    {
      title: "8. Termination",
      content: [
        "Burj AI may suspend or terminate access to the platform at its discretion, for violations of these Terms or illegal activities.",
      ],
    },
    {
      title: "9. Governing Law & Dispute Resolution",
      content: [
        "These Terms are governed by the laws of the jurisdiction where Burj AI is registered. Any disputes shall be resolved in the courts of that jurisdiction unless agreed otherwise in writing.",
      ],
    },
    {
      title: "10. Contact Information",
      content: [
        "For questions about these Terms, contact us at support@burjai.in or visit our offices at your registered address.",
      ],
    },
  ],
};

export default TermsAndConditionsPage;
